import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setNumStudents, setDisorderLevel, setStartingSituation, setInterventionControl,
    setLogFileName, setEnableAllScripts,
    setUseSpeechTools,
    selectNumStudents, selectDisorderLevel, selectStartingSituation, selectInterventionControl,
    selectLogFileName, selectEnableAllScripts,
    selectUseSpeechTools
} from './ConfigurationOptionsSlice';
import {
    selectClassroom, selectChair, selectFront, selectOutdoor, selectMessiness,
    setClassroom, setChair, setFront, setOutdoor, setMessiness
} from './ConfigureRoomModulesSlice'
import { selectNoiseLevel } from '../classState/studentsSlice';
import { selectWeather, setWeather } from '../SessionSettings/WeatherSlice';


/**
 * SessionSettingsManager is a React component that handles session settings, allowing the user to:
 * - Save the current configuration to a JSON file.
 * - Load session configuration from a JSON file.
 * - Reset all configuration options to default values.
 *
 * @returns {object} - Returns methods to save, load, and reset session settings.
 */
const SessionSettingsManager = () => {
    const dispatch = useDispatch();

    // Get session options from the Redux Store
    const numStudents = useSelector(selectNumStudents);
    const disorderLevel = useSelector(selectDisorderLevel);
    const startingSituation = useSelector(selectStartingSituation);
    const interventionControl = useSelector(selectInterventionControl);

    const noiseLevel = useSelector(selectNoiseLevel);
    const weather = useSelector(selectWeather);

    const logFileName = useSelector(selectLogFileName);
    const enableAllScripts = useSelector(selectEnableAllScripts);
    const useSpeechTools = useSelector(selectUseSpeechTools);

    const selectedChair = useSelector(selectChair);
    const selectedClassroom = useSelector(selectClassroom);
    const selectedFront = useSelector(selectFront);
    const selectedOutdoor = useSelector(selectOutdoor);
    const messiness = useSelector(selectMessiness);

    // Save current settings
    const saveSettings = () => {
        console.log("save settings")
        const settings = {
            numStudents,
            disorderLevel,
            startingSituation,
            interventionControl,
            noiseLevel,
            weather,
            //useEventLogging,
            logFileName,
            enableAllScripts,
            useSpeechTools,

            roomModuleOptions: {
                chair: selectedChair,
                classroom: selectedClassroom,
                front: selectedFront,
                outdoor: selectedOutdoor,
                messiness: messiness
            }
        };

        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(settings))}`;
        const link = document.createElement('a');
        link.href = jsonString;
        link.download = 'session-settings.json';
        link.click();
    };

    // Load settings from JSON file
    const loadSettings = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const settings = JSON.parse(e.target.result);
                dispatch(setNumStudents(settings.numStudents));
                dispatch(setDisorderLevel(settings.disorderLevel));
                dispatch(setStartingSituation(settings.startingSituation));
                dispatch(setInterventionControl(settings.interventionControl));
                dispatch(setWeather(settings.weather));
                dispatch(setLogFileName(settings.logFileName));
                //dispatch(setUseEventLogging(settings.useEventLogging));
                dispatch(setEnableAllScripts(settings.enableAllScripts));
                dispatch(setUseSpeechTools(settings.useSpeechTools));

                if (settings.roomModuleOptions) {
                    dispatch(setChair(settings.roomModuleOptions.chair));
                    dispatch(setClassroom(settings.roomModuleOptions.classroom));
                    dispatch(setFront(settings.roomModuleOptions.front));
                    dispatch(setOutdoor(settings.roomModuleOptions.outdoor));
                    dispatch(setMessiness(settings.roomModuleOptions.messiness));
                }
            };
            reader.readAsText(file);
        }
    };

    // Create file input for loading a file
    const triggerLoadFile = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = loadSettings;
        input.click();
    };

    // Reset to default settings
    const resetToDefaultSettings = () => {
        dispatch({ type: 'configuration/resetDefaults' });
        dispatch({ type: 'roomModules/resetDefaults' });
    };

    return { saveSettings, triggerLoadFile, resetToDefaultSettings };
};

export default SessionSettingsManager;
